import { useContext, useMemo } from 'react';
import defaultDates from '../../../utils/defaultDates';
import useHierarchyData from '../../../hooks/useHierarchyData';
import toMillis from '../../../helpers/dataHelpers/matrixDataHelpers/toMillis';
import ScaleMomentVisualisation from '../../Visualisations/moment/ScaleMomentVisualisation';
import EventFieldInfoPopover from './EventFieldInfoPopover';
import ChartDataTable from '../ChartDataTable';
import { averagesTable } from '../../../helpers/dataHelpers/chartDataTable';
import LiveReportRenderContext from '../../../StateManagment/LiveReportRenderContext';
import { buildTemplateEventResult } from './templateEventRecords';

// AI-CONTEXT: See docs/features/api-data-fetching.md

const EventScaleData = ({ field, color, primaryColorOverride, graphColor, startDate, endDate, activityId, templateActivityData }) => {
  const isTemplate = !!templateActivityData;
  const inLiveReport = useContext(LiveReportRenderContext);
  const resolvedColor = primaryColorOverride ?? color ?? graphColor;

  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), []);
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw]);
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw]);
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs]);
  const endMs = useMemo(() => toMillis(endDate) ?? deMs, [endDate, deMs]);

  const live = useHierarchyData({
    syncType: 'Event',
    id: isTemplate ? '' : field.ID,
    startDate: startMs,
    endDate: endMs,
  });

  const template = useMemo(
    () => buildTemplateEventResult(templateActivityData, field.ID),
    [templateActivityData, field.ID]
  );

  const records = isTemplate ? (template?.records || []) : live.records;
  const projectHierarchy = isTemplate ? (template?.hierarchy || {}) : live.hierarchy;
  const loading = isTemplate ? false : live.loading;

  const data = useMemo(() => {
    if (loading || !records.length) {
      return { groupedData: [], projectAverages: {}, totalResponses: 0 };
    }

    const scoresByProject = records.reduce((acc, r) => {
      const name = r._projectName || 'Onbekend';
      (acc[name] ||= []).push(Number(r.Value));
      return acc;
    }, {});

    const projectAverages = Object.fromEntries(
      Object.entries(scoresByProject).map(([name, arr]) => {
        const valid = arr.filter(v => Number.isFinite(v));
        const avg = valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
        return [name, Number.isFinite(avg) ? avg.toFixed(1) : '0.0'];
      })
    );

    const allValues = records.map(r => Number(r.Value)).filter(Number.isFinite);
    const globalAvg = allValues.length ? allValues.reduce((a, b) => a + b, 0) / allValues.length : 0;

    return {
      groupedData: [{ moment: 'gemiddelde', average: Number(globalAvg.toFixed(2)), count: allValues.length }],
      projectAverages,
      totalResponses: records.length,
    };
  }, [records, loading]);

  return (
    <>
      {!inLiveReport && (field?.Title || activityId) && (
        <div className='home-kpi-question-title-container'>
          {field?.Title && <p className='event-field-title'>{field.Title}</p>}
          <EventFieldInfoPopover field={field} activityId={activityId} totalResponses={data.totalResponses} projectHierarchy={projectHierarchy} />
        </div>
      )}
      <ScaleMomentVisualisation
        data={data}
        field={field.ID}
        projectHierarchy={projectHierarchy}
        projectAverages={data.projectAverages}
        primaryColorOverride={resolvedColor}
        source="event"
      />
      {/* Eén gemiddelde over alle registraties; de grafiek noemt dat
          'gemiddelde', de tabel zegt waarover. */}
      <ChartDataTable table={averagesTable((data.groupedData || []).map(g => ({ ...g, moment: 'Alle registraties' })))} />
    </>
  );
};

export default EventScaleData;
